import { createSlice } from '@reduxjs/toolkit';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
//
import { dispatch, store } from '../store';
import { updateError } from './user';
import { upsertWidgetCounter } from './widgetCounter';
import { fetchContactDetails } from '../../pages/whatsapp/whatsappUtil';

// 2026-05-06 P0 — held-Enter fired sendWhatsappMessage twice ~18 ms apart,
// before whatsppSendLoading flipped, so two outbounds hit Meta and two
// WhatsappMessage rows landed. The synchronous Set is the *first* gate
// (closes before any await); the BE accepts the same UUID for 60s and
// returns the original message on a replay. Either layer alone leaks —
// the FE latch is too eager (e.g. component remount), the BE alone still
// burns network.
const waSendInflightKeys = new Set();
const waGenIdemKey = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

export const WHATSAPP_FILTERS = Object.freeze({
    "ALL_FILTER": "ALL_FILTER",
    "UNREAD_FILTER": "UNREAD_FILTER",
    "PENDING_FILTER": "PENDING_FILTER",
    "NEW_LEAD_FILTER": "NEW_LEAD_FILTER",
    "ARCHIVED_FILTER": "ARCHIVED_FILTER",
    "CAMPAIGN_FILTER": "CAMPAIGN_FILTER"
});

const SEARCH_MODE_PARAMS = Object.freeze({
    '': '&is_closed=false&lead__is_archived=false&campaign_or_pending=true',
    'FOLLOW_UP': '&is_closed=false&is_unread=true',
    'PENDING': '&is_closed=false&is_pending=true',
    'TAG': '&is_closed=false',
    'MY': '&is_closed=false',
    'CLOSED': '&is_closed=true',
    'ARCHIVED': '&lead__is_archived=true',
    'CAMPAIGN': '&is_closed=false&campaign__isnull=false',
});
// ---------------------------------------------------------------------
const initialState = {
    // loading states
    isLoading: false,
    threadLoading: false,
    threadNextLoading: false,
    whatsappFiltersLoading: false,
    whatsappSearchLeadLoading: false,
    whatsappChatScreenLoading: false,
    whatsappChatNextAPILoading: false,
    whatsppSendLoading: false,
    // current values
    currentBusinessId: -1,
    currentWhatsappPhoneId: -1,
    currentFilter: WHATSAPP_FILTERS.ALL_FILTER,
    currentSearch: "",
    threadMessages: [],
    threadNextAPI: "",
    filterCounts: {},
    currentWhatsappTemplate: [],
    whatsappChats: [],
    whatsAppChatNextAPI: "",
    whatsAppChatMetaData: {},
    // total value maps
    whatsappJwtTokens: {},
    whatsppTemplates: {},
    businessIdToPhoneMap: {},
    // search results
    leadSearchResult: [],
    pieSocketSubscriptions: [],
    // not configured (no WhatsApp Business setup for this practice)
    whatsappNotConfigured: false,
    // notif count
    whatsappNotifCount: 0,
    // searchMode
    searchMode: "",
    currentTag: "",
    currentUser: null,
    practiceChoices: [],
    allowedUsageTypes: null, // null = show all (backward compatible), array = filter by usage_type
    allowedEmailUsageTypes: null, // null = show all, array = filter email templates by usage_type
    // AI Configuration (legacy — used by CallScriptV2 AI assistant, loaded from practice choices)
    aiConfig: null,
    aiConfigLoading: false,
    // AI Wizard Configuration (loaded from /ai-config/ endpoint, separate from legacy aiConfig)
    aiWizardConfig: null,
    aiWizardConfigLoading: false,
    aiWizardStatus: null, // { wizard_enabled: bool, wizard_completed: bool }
    // AI Assistant
    aiAssistantLoading: false,
    aiAssistantResult: null,
    aiAssistantError: null,
    aiAssistantActionType: null,
    // Service-window-closed dialog payload. Set when the backend rejects a
    // free-form send with HTTP 409 (PMP rules — Meta won't deliver outside
    // the 24h window). Cleared by ChatPage when the operator dismisses the
    // dialog or switches to a template.
    serviceWindowClosedDialog: null,
    // Track C / F8 Message Info — keyed by message id. Populated lazily by
    // fetchMessageInfo() when the operator opens the Info drawer. Shape:
    //   { [messageId]: { sent_at, delivered_at, read_at, failed_at } | null }
    // null means the fetch is in flight (loading); undefined means not yet
    // requested.
    messageInfo: {},
    messageInfoLoading: {},
};

const slice = createSlice({
    name: 'whatsApp',
    initialState,
    reducers: {
        // For Reseting the reducer
        reset(state) {
            Object.assign(state, initialState);
        },
        startInitializeWhatsapp(state) {
            Object.assign(state, initialState);
            state.isLoading = true;
        },
        completeWhatsappInitialization(state, action) {
            const { payload } = action ?? {};
            state.businessIdToPhoneMap = payload?.businessIdToPhoneMap ?? {};
            state.currentBusinessId = payload?.currentBusinessId ?? -1;
            state.currentWhatsappPhoneId = payload?.currentPhoneid ?? -1;
            state.whatsappJwtTokens = payload?.jwtTokens ?? {};
            state.whatsppTemplates = payload?.templates ?? {};
            state.isLoading = false;
            state.currentWhatsappTemplate = payload?.currentWhatsappTemplate ?? [];
            state.practiceChoices = payload?.practiceChoices ?? [];
            state.allowedUsageTypes = payload?.allowedUsageTypes ?? null;
            state.allowedEmailUsageTypes = payload?.allowedEmailUsageTypes ?? null;
            state.aiConfig = payload?.aiConfig ?? null;
            // Mark as not configured only when the API explicitly returned no business account.
            // When payload is empty (error fallback), leave whatsappNotConfigured unchanged
            // so transient errors don't falsely show the "not configured" empty state.
            if (payload && 'currentBusinessId' in payload) {
                state.whatsappNotConfigured = payload.currentBusinessId === -1;
            }
        },
        setAiConfig(state, action) {
            state.aiConfig = action.payload;
        },
        setAiConfigLoading(state, action) {
            state.aiConfigLoading = action.payload;
        },
        setAiWizardConfig(state, action) {
            state.aiWizardConfig = action.payload;
        },
        setAiWizardConfigLoading(state, action) {
            state.aiWizardConfigLoading = action.payload;
        },
        setAiWizardStatus(state, action) {
            state.aiWizardStatus = action.payload;
        },
        setTemplateFilterConfigs(state, action) {
            const { allowedUsageTypes, allowedEmailUsageTypes } = action.payload ?? {};
            state.allowedUsageTypes = allowedUsageTypes ?? null;
            state.allowedEmailUsageTypes = allowedEmailUsageTypes ?? null;
        },
        startFetchWhatsappThreads(state) {
            state.threadLoading = true;
        },
        completeFetchWhatsappThreads(state, action) {
            const { payload } = action ?? {}
            state.threadLoading = false;
            state.threadMessages = payload?.threadMessages ?? [];
            state.threadNextAPI = payload?.threadNext ?? "";
            state.isLoading = false;
        },
        startFetchWhatsappFilterCount(state) {
            state.whatsappFiltersLoading = true;
        },
        completeFetchWhatsappFilterCount(state, action) {
            const { payload } = action ?? {};
            state.whatsappFiltersLoading = false;
            state.filterCounts = payload?.filterCounts ?? {};
            state.isLoading = false;
        },
        startThreadNextFetch(state) {
            state.threadNextLoading = true;
        },
        completeThreadNextFetch(state, action) {
            const { payload } = action ?? {}
            state.threadNextLoading = false;
            state.threadMessages = payload?.threadMessages ?? state.threadMessages;
            state.threadNextAPI = payload?.threadNext ?? state.threadNext;
            state.isLoading = false;
        },
        changeCurrentFilter(state, action) {
            const { payload } = action ?? {}
            state.currentFilter = payload?.changedFilter;
            state.threadLoading = true;
        },
        changeCurrentSearch(state, action) {
            const { payload } = action ?? {}
            state.currentSearch = payload?.changedSearch
        },
        changeCurrentPhoneAndBusiness(state, action) {
            const { payload } = action ?? {}
            state.currentBusinessId = payload?.currentBusinessId ?? -1;
            state.currentWhatsappPhoneId = payload?.currentWhatsappPhoneId ?? -1;
            state.currentWhatsappTemplate = payload?.currentWhatsappTemplate;
            state.currentFilter = WHATSAPP_FILTERS.ALL_FILTER;
            state.currentSearch = "";
            state.threadMessages = [];
            state.threadNextAPI = "";
            state.filterCounts = {};
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
        },
        saveSyncedTemplates(state, action) {
            const { payload } = action ?? {};
            state.currentWhatsappTemplate = payload?.currentWhatsappTemplate ?? [];
            state.whatsppTemplates = payload?.whatsppTemplates;
        },
        startLeadSearchResults(state) {
            state.whatsappSearchLeadLoading = true;
            state.leadSearchResult = [];
        },
        storeLeadSearchResults(state, action) {
            const { payload } = action ?? {};
            state.leadSearchResult = payload?.data;
            state.whatsappSearchLeadLoading = false;
        },
        saveWhatsappChatMetaData(state, action) {
            const { payload } = action ?? {};
            state.whatsAppChatMetaData = payload;
        },
        startWhatsappChatLoad(state) {
            state.whatsappChatScreenLoading = true
        },
        completeWhatsapChatLoad(state, action) {
            const { payload } = action ?? {};
            state.whatsappChatScreenLoading = false;
            state.whatsappChats = payload?.whatsappChats ?? [];
            state.whatsAppChatMetaData = payload?.whatsAppChatMetaData ?? {};
            state.whatsAppChatNextAPI = payload?.whatsAppChatNextAPI ?? ""
        },
        refreshTagInfo(state, action) {
            const { payload } = action ?? {};
            state.whatsAppChatMetaData = payload?.whatsAppChatMetaData ?? {};
        },
        errorWhatsappChatLoad(state) {
            state.whatsappChatScreenLoading = false;
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
        },
        resetWhatsappChat(state) {
            state.whatsappChatScreenLoading = false;
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
        },
        startWhatsappNextMessageFetch(state) {
            state.whatsappChatNextAPILoading = true
        },
        completeWhatsappNextMessageFetch(state, action) {
            const { payload } = action ?? {};
            state.whatsappChatNextAPILoading = false;
            state.whatsappChats = payload?.whatsappChats;
            state.whatsAppChatNextAPI = payload?.whatsAppChatNextAPI;
        },
        toggleWhatsappSendLoading(state, action) {
            state.whatsppSendLoading = action?.payload ?? false;
        },
        showServiceWindowClosedDialog(state, action) {
            state.serviceWindowClosedDialog = action?.payload ?? null;
        },
        clearServiceWindowClosedDialog(state) {
            state.serviceWindowClosedDialog = null;
        },
        saveLeadDetails(state, action) {
            const { payload } = action ?? {};
            state.whatsAppChatMetaData = payload ?? {};
        },
        saveThreadDetails(state, action) {
            const { payload } = action ?? {};
            state.threadMessages = payload ?? {};
        },
        saveMessageDetails(state, action) {
            const { payload } = action ?? {};
            state.whatsappChats = payload ?? {};
        },
        updatePieSocketSubscriptions(state, action) {
            state.pieSocketSubscriptions = action?.payload ?? []
        },
        addWhatsappNotifCount(state, action) {
            state.whatsappNotifCount = action?.payload ?? 0
        },
        updateSearchMode(state, action) {
            state.searchMode = action?.payload?.mode;
            state.currentUser = action?.payload?.user;
        },
        updateSearchTag(state, action) {
            state.currentTag = action?.payload
        },
        // AI Assistant reducers
        startAiAssistant(state, action) {
            state.aiAssistantLoading = true;
            state.aiAssistantError = null;
            state.aiAssistantResult = null;
            state.aiAssistantActionType = action?.payload ?? null;
        },
        completeAiAssistant(state, action) {
            state.aiAssistantLoading = false;
            state.aiAssistantResult = action?.payload;
        },
        failAiAssistant(state, action) {
            state.aiAssistantLoading = false;
            state.aiAssistantError = action?.payload;
        },
        clearAiAssistant(state) {
            state.aiAssistantLoading = false;
            state.aiAssistantResult = null;
            state.aiAssistantError = null;
            state.aiAssistantActionType = null;
        },
        removeThreadFromList(state, action) {
            const threadId = action.payload;
            state.threadMessages = (state.threadMessages ?? []).filter(
                (t) => String(t?.id ?? t?.thread_id) !== String(threadId)
            );
        },
        // Bulk variant — same filter logic but accepts an array of ids.
        // Used by `bulkArchiveThreads` to optimistically remove all
        // succeeded threads from the visible list in a single render.
        removeThreadsFromList(state, action) {
            const ids = Array.isArray(action.payload) ? action.payload : [];
            if (ids.length === 0) return;
            const idSet = new Set(ids.map((x) => String(x)));
            state.threadMessages = (state.threadMessages ?? []).filter(
                (t) => !idSet.has(String(t?.id ?? t?.thread_id))
            );
        },
        // Apply read state across many threads (bulk mark_as_read).
        // Mirrors the per-row optimistic update done by markAsRead's
        // refresh path: clear `unread_count` and flip `is_read=true`.
        applyThreadsRead(state, action) {
            const ids = Array.isArray(action.payload) ? action.payload : [];
            if (ids.length === 0) return;
            const idSet = new Set(ids.map((x) => String(x)));
            (state.threadMessages ?? []).forEach((t) => {
                if (t && idSet.has(String(t?.id ?? t?.thread_id))) {
                    t.is_read = true;
                    t.unread_count = 0;
                }
            });
        },
        // Inverse — flip `is_read=false` so the unread badge reappears.
        applyThreadsUnread(state, action) {
            const ids = Array.isArray(action.payload) ? action.payload : [];
            if (ids.length === 0) return;
            const idSet = new Set(ids.map((x) => String(x)));
            (state.threadMessages ?? []).forEach((t) => {
                if (t && idSet.has(String(t?.id ?? t?.thread_id))) {
                    t.is_read = false;
                }
            });
        },
        applyMessageReaction(state, action) {
            const { messageId, emoji, removed, byUser } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            const target = list[idx];
            if (removed || !emoji) {
                target.whatsapp_message_reaction = null;
            } else {
                target.whatsapp_message_reaction = {
                    ...(target.whatsapp_message_reaction || {}),
                    emoji,
                    is_outbound: true,
                    reacted_by: byUser ?? target.whatsapp_message_reaction?.reacted_by ?? null,
                };
            }
        },
        applyMessageRevoked(state, action) {
            const { messageId } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_revoked = true;
            list[idx].body = '';
        },
        revertMessageRevoked(state, action) {
            const { messageId, body } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_revoked = false;
            list[idx].body = body ?? '';
        },
        applyMessageStarred(state, action) {
            const { messageId, starredBy } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_starred = true;
            if (starredBy) {
                list[idx].starred_by = starredBy;
            }
        },
        applyMessageUnstarred(state, action) {
            const { messageId } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_starred = false;
            list[idx].starred_by = null;
        },
        applyMessagePinned(state, action) {
            const { messageId, threadId, pinnedAt, pinnedMessage } = action.payload ?? {};
            if (messageId == null && pinnedMessage == null) return;
            // Update the metadata for the currently-open thread only — other
            // threads' pins arrive on their own selectMeta load. The reducer
            // never touches threads that aren't visible.
            const meta = state.whatsAppChatMetaData ?? {};
            if (
                threadId != null
                && meta?.thread
                && String(meta.thread.id) === String(threadId)
            ) {
                meta.thread.pinned_message = pinnedMessage ?? meta.thread.pinned_message ?? { id: messageId };
                meta.thread.pinned_at = pinnedAt ?? meta.thread.pinned_at ?? null;
            }
        },
        applyMessageUnpinned(state, action) {
            const { threadId } = action.payload ?? {};
            const meta = state.whatsAppChatMetaData ?? {};
            if (
                threadId != null
                && meta?.thread
                && String(meta.thread.id) === String(threadId)
            ) {
                meta.thread.pinned_message = null;
                meta.thread.pinned_at = null;
            }
        },
        // Track C / F6: optimistic body swap when the operator hits Save on
        // the inline editor. Surgical — touches only the matching message and
        // never re-shapes the array, so memoised consumers downstream don't
        // re-render every bubble. Mirrors the Track A reaction reducer style.
        applyMessageEdited(state, action) {
            const { messageId, newBody, editedAt } = action.payload ?? {};
            if (!messageId) return;
            const idx = (state.whatsappChats ?? []).findIndex(
                (m) => `${m?.id}` === `${messageId}`,
            );
            if (idx === -1) return;
            const msg = state.whatsappChats[idx];
            // Prefer to mutate in place (createSlice + Immer makes this safe).
            // We DO NOT clear the previous body until the optimistic update
            // is reverted, because a Sentry'd 502 would otherwise blank the
            // bubble and confuse the operator mid-edit.
            state.whatsappChats[idx] = {
                ...msg,
                body: newBody,
                is_edited: true,
                edited_at: editedAt || msg?.edited_at,
            };
        },
        // Track C / F6: surgical revert when the editMessage thunk catches a
        // server failure. Restores the body the slice held BEFORE the
        // optimistic update; thunk is responsible for handing us the previous
        // body via payload.previousBody.
        revertMessageEdited(state, action) {
            const { messageId, previousBody, previousIsEdited, previousEditedAt } = action.payload ?? {};
            if (!messageId) return;
            const idx = (state.whatsappChats ?? []).findIndex(
                (m) => `${m?.id}` === `${messageId}`,
            );
            if (idx === -1) return;
            const msg = state.whatsappChats[idx];
            state.whatsappChats[idx] = {
                ...msg,
                body: previousBody ?? '',
                is_edited: previousIsEdited ?? msg?.is_edited ?? false,
                edited_at: previousEditedAt ?? msg?.edited_at ?? null,
            };
        },
        // Track C / F8: payloads from GET .../info/. Set messageInfo[id]=null
        // before the fetch (loading) and to the data afterwards.
        startMessageInfoFetch(state, action) {
            const messageId = action.payload;
            if (!messageId) return;
            state.messageInfoLoading = { ...(state.messageInfoLoading ?? {}), [messageId]: true };
        },
        completeMessageInfoFetch(state, action) {
            const { messageId, info } = action.payload ?? {};
            if (!messageId) return;
            state.messageInfo = { ...(state.messageInfo ?? {}), [messageId]: info };
            state.messageInfoLoading = { ...(state.messageInfoLoading ?? {}), [messageId]: false };
        },
        failMessageInfoFetch(state, action) {
            const messageId = action.payload;
            if (!messageId) return;
            state.messageInfoLoading = { ...(state.messageInfoLoading ?? {}), [messageId]: false };
        },
    },
});

// Reducer
export default slice.reducer;

export const {
    changeCurrentSearch,
    changeCurrentPhoneAndBusiness,
    saveWhatsappChatMetaData,
    resetWhatsappChat,
    updatePieSocketSubscriptions,
    updateSearchMode,
    updateSearchTag,
    clearAiAssistant,
    removeThreadFromList,
    removeThreadsFromList,
    applyThreadsRead,
    applyThreadsUnread,
    clearServiceWindowClosedDialog,
    applyMessageReaction,
    applyMessageRevoked,
    revertMessageRevoked,
    applyMessageStarred,
    applyMessageUnstarred,
    applyMessagePinned,
    applyMessageUnpinned,
    applyMessageEdited,
    revertMessageEdited,
} = slice.actions;

const getPracticeId = () => {
    const id = store.getState()?.practiceTreatmentList?.practiceId;
    if (!id || id === 'null' || id === 'undefined') return null;
    return id;
};

const getWhatsappThreadsFromStore = () => store.getState().whatsappV2?.threadMessages;

const fetchFilterCounts = async (startLoadingState = true) => {
    if (startLoadingState)
        dispatch(slice.actions.startFetchWhatsappFilterCount());

    const filterCounts = {};

    try {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const currentBusinessId = state?.currentBusinessId;
        const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

        if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
            dispatch(slice.actions.completeFetchWhatsappFilterCount({ filterCounts }));
            return;
        }

        const currentSearch = state?.currentSearch;
        const baseUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/`;

        // Fetch unread, pending, archived, closed AND window-closed counts
        // in parallel for the empty-state hub widgets. The
        // service_window_closed BE filter shipped earlier in the
        // last_inbound_at hardening sprint and is live on production.
        const searchPrefix = currentSearch?.length ? `search/?query=${encodeURIComponent(currentSearch)}&` : '?';
        const [unreadResponse, pendingResponse, archivedResponse, closedResponse, windowClosedResponse] = await Promise.all([
            callApi(`${baseUrl}${searchPrefix}is_closed=false&is_unread=true&is_archived_included=true&fields={}&limit=1`, 'GET'),
            callApi(`${baseUrl}${searchPrefix}is_closed=false&is_pending=true&is_archived_included=true&fields={}&limit=1`, 'GET'),
            callApi(`${baseUrl}${searchPrefix}lead__is_archived=true&fields={}&limit=1`, 'GET'),
            callApi(`${baseUrl}${searchPrefix}is_closed=true&fields={}&limit=1`, 'GET'),
            callApi(`${baseUrl}${searchPrefix}service_window_closed=true&is_closed=false&fields={}&limit=1`, 'GET'),
        ]);
        filterCounts.FOLLOW_UP_COUNT = unreadResponse?.data?.count || 0;
        filterCounts.PENDING_COUNT = pendingResponse?.data?.count || 0;
        filterCounts.ARCHIVED_COUNT = archivedResponse?.data?.count || 0;
        filterCounts.CLOSED_COUNT = closedResponse?.data?.count || 0;
        filterCounts.WINDOW_CLOSED_COUNT = windowClosedResponse?.data?.count || 0;

    } catch (err) {
        console.error("Error while fetching whatsapp filter counts", err);
    }

    dispatch(slice.actions.completeFetchWhatsappFilterCount({ filterCounts }));
};

export function handleAddWhatsappNotificationCount(reset = false) {
    return async () => {
        const whatsappNotifCount = store.getState()?.whatsappV2?.whatsappNotifCount ?? 0;
        if (reset)
            dispatch(slice.actions.addWhatsappNotifCount(0));
        else
            dispatch(slice.actions.addWhatsappNotifCount(whatsappNotifCount + 1));
    }
}

// Fetch WhatsApp counts for widget/dashboard (unread, new leads, pending)
export function fetchUnreadWhatsAppCount() {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return { unread: 0, newLeads: 0, pending: 0 };
        try {
            const practiceId = getPracticeId();
            if (!practiceId) return { unread: 0, newLeads: 0, pending: 0 };

            // First get WhatsApp business accounts
            const accountsResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`
            );
            const accounts = accountsResponse?.data?.results || [];
            if (accounts.length === 0) return { unread: 0, newLeads: 0, pending: 0 };

            const businessId = accounts[0]?.id;
            if (!businessId) return { unread: 0, newLeads: 0, pending: 0 };

            // Get phones for this business account
            const phonesResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/`
            );
            const phones = phonesResponse?.data?.results || [];
            if (phones.length === 0) return { unread: 0, newLeads: 0, pending: 0 };

            const phoneId = phones[0]?.id;
            if (!phoneId) return { unread: 0, newLeads: 0, pending: 0 };

            const baseUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/`;

            // Fetch all three counts in parallel with limit=1 to minimize payload (we only need the count)
            const [unreadResponse, newLeadsResponse, pendingResponse] = await Promise.all([
                callApi(`${baseUrl}?is_unread=true&is_closed=false&is_archived_included=true&fields={}&limit=1`),
                callApi(`${baseUrl}?lead__isnull=true&is_closed=false&is_archived_included=true&fields={}&limit=1`),
                callApi(`${baseUrl}?is_pending=true&is_closed=false&is_archived_included=true&fields={}&limit=1`),
            ]);

            const unreadCount = unreadResponse?.data?.count || 0;
            const newLeadsCount = newLeadsResponse?.data?.count || 0;
            const pendingCount = pendingResponse?.data?.count || 0;

            // Update the filter counts in store
            const currentFilterCounts = store.getState()?.whatsappV2?.filterCounts || {};
            dispatch(slice.actions.completeFetchWhatsappFilterCount({
                filterCounts: {
                    ...currentFilterCounts,
                    [WHATSAPP_FILTERS.UNREAD_FILTER]: unreadCount,
                    [WHATSAPP_FILTERS.NEW_LEAD_FILTER]: newLeadsCount,
                    [WHATSAPP_FILTERS.PENDING_FILTER]: pendingCount,
                    FOLLOW_UP_COUNT: unreadCount,
                    PENDING_COUNT: pendingCount,
                }
            }));

            // Store in widgetCounter so dashboard reads stable counts
            // independent of WhatsApp page filter state
            dispatch(upsertWidgetCounter({
                key: '7',
                count: unreadCount + newLeadsCount + pendingCount,
                whatsapp: { unread: unreadCount, newLeads: newLeadsCount, pending: pendingCount }
            }));

            return { unread: unreadCount, newLeads: newLeadsCount, pending: pendingCount };
        } catch (error) {
            // Silent fail - widget will show 0
            return { unread: 0, newLeads: 0, pending: 0 };
        }
    }
}

export function syncTemplates() {
    return async () => {
        try {
            dispatch(updateError({ message: "Template Sync In Progress!", success: "warning" }));
            const practiceId = getPracticeId();
            const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
            if (!currentBusinessId || currentBusinessId === -1) {
                dispatch(updateError({ message: "WhatsApp is still loading, please try again", success: "warning" }));
                return;
            }
            const whatsppTemplates = store.getState()?.whatsappV2?.whatsppTemplates;
            await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/sync_templates/`,
                'POST'
            );
            const templateResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-message-templates/`
            );
            const currentTemplateMap = { ...whatsppTemplates };
            currentTemplateMap[currentBusinessId] = templateResponse?.data?.results ?? [];
            dispatch(slice.actions.saveSyncedTemplates({ whatsppTemplates: currentTemplateMap, currentWhatsappTemplate: templateResponse?.data?.results ?? [] }))
            dispatch(updateError({ message: "Successfully synced the templates!", success: true }));
        } catch (error) {
            console.error("Failed to sync templates");
            dispatch(updateError({ message: "Failed to sync templates!", success: false }));
        }
    }
}
const unsubscribeConnections = async (subs) => {
    try {
        subs.forEach(sub => {
            if (sub.connection) {
                sub.connection.unsubscribe(sub?.roomId);
            }
        });
    } catch (error) {
        console.error("Error in unsubscribing connection")
    }
}

export function initializeWhatsapp() {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        try {
            const updatePieSocketSubscriptions = store.getState()?.whatsappV2?.pieSocketSubscriptions;
            dispatch(slice.actions.startInitializeWhatsapp());
            const practiceId = getPracticeId();
            await unsubscribeConnections(updatePieSocketSubscriptions);
            // Get all Available Whatsapp Business Accounts
            const availableWhatsaAppBusinessAccResp = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`,
                'GET'
            );

            const businessIdList = availableWhatsaAppBusinessAccResp?.data?.results?.map(acct => acct?.id);
            if (!businessIdList?.length) {
                // dispatch(updateError({ message: "No Whatsapp Business Account Found!", success: false }));
                throw new Error("No Whatsapp Business Account Found!");
            }

            const businessIdToPhone = {};

            const fetchAllBusinessData = async () => {
                const results = await Promise.all(
                    businessIdList.map(async (businessId) => {
                        const availablePhoneNumbersWithBID = await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/`,
                            'GET'
                        );
                        return { businessId, data: availablePhoneNumbersWithBID?.data?.results ?? [] };
                    })
                );

                results?.forEach(({ businessId, data }) => {
                    businessIdToPhone[businessId] = data;
                });

                return businessIdToPhone;
            };

            await fetchAllBusinessData();

            const defaultBusinessId = Object.keys(businessIdToPhone ?? {})?.[0] ?? -1;
            const defaultPhoneId = businessIdToPhone?.[defaultBusinessId]?.[0]?.id ?? -1;

            if (defaultBusinessId === -1 || defaultPhoneId === -1) {
                // dispatch(updateError({ message: "No Whatsapp Phone Number Found!", success: false }));
                throw new Error("No Whatsapp Phone Number Found!")
            }

            const jwtTokens = {};
            const templates = {};
            let defaultTemplates = [];

            const fetchJWTTokensAndTemplates = async () => {
                await Promise.all(
                    Object.keys(businessIdToPhone ?? {}).map(async (businessId) => {
                        const phoneIdObj = {};

                        await Promise.all(
                            (businessIdToPhone?.[businessId] ?? []).map(async (phoneObj) => {
                                const jwtResp = await callApi(
                                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneObj?.id}/generate_websocket_jwt/`,
                                    'POST'
                                );
                                phoneIdObj[phoneObj.id] = jwtResp?.data?.auth;
                            })
                        );

                        const templateResponse = await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-message-templates/`
                        );

                        jwtTokens[businessId] = phoneIdObj;
                        templates[businessId] = templateResponse?.data?.results ?? [];
                        if (businessId === defaultBusinessId) {
                            defaultTemplates = templateResponse?.data?.results ?? [];
                        }
                    })
                );
            };

            await fetchJWTTokensAndTemplates();

            const practiceChoices = await callApi(`/api/v1/practices/${practiceId}/choices/`);

            const choices = practiceChoices?.data?.results?.filter(choice => choice?.metadata?.action === "close_thread" && choice?.metadata?.resource === "whatsapp_message_thread")?.[0]?.items?.map(choice => choice?.value ?? "");

            // Extract AI Config from practice choices
            const aiConfigChoice = practiceChoices?.data?.results?.find(choice => /ai\s*config/i.test(String(choice?.name || '')));
            const aiConfig = aiConfigChoice?.metadata || null;

            // Extract WhatsApp template usage type filter from practice choices
            const waTemplateConfig = practiceChoices?.data?.results?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'whatsapp_template'
            );
            const allowedUsageTypes = waTemplateConfig?.items?.length > 0
                ? waTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            // Extract Email template usage type filter from practice choices
            const emailTemplateConfig = practiceChoices?.data?.results?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'email_template'
            );
            const allowedEmailUsageTypes = emailTemplateConfig?.items?.length > 0
                ? emailTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            dispatch(slice.actions.completeWhatsappInitialization({
                businessIdToPhoneMap: businessIdToPhone,
                currentBusinessId: defaultBusinessId,
                currentPhoneid: defaultPhoneId,
                jwtTokens,
                templates,
                currentWhatsappTemplate: defaultTemplates,
                practiceChoices: choices,
                aiConfig,
                allowedUsageTypes,
                allowedEmailUsageTypes,
            }))

        } catch (error) {
            console.error("Error in initializeWhatsapp", error);
            // Only stop loading — do NOT set currentBusinessId: -1 here, as that
            // falsely marks the practice as "not configured" on transient errors.
            dispatch(slice.actions.completeWhatsappInitialization());
        }
    }
}

// Fetch AI wizard status for a practice
export function fetchAIWizardStatus(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/ai-config/wizard-status/`);
            if (response.ok) {
                dispatch(slice.actions.setAiWizardStatus(response.data));
            }
        } catch (error) {
            console.error("Error fetching AI wizard status", error);
        }
    };
}

// Fetch AI wizard config for a practice (from /ai-config/ endpoint)
export function fetchAIConfig(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            dispatch(slice.actions.setAiWizardConfigLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/ai-config/`);
            if (response.ok) {
                dispatch(slice.actions.setAiWizardConfig(response.data));
            }
        } catch (error) {
            console.error("Error fetching AI config", error);
        } finally {
            dispatch(slice.actions.setAiWizardConfigLoading(false));
        }
    };
}

// Update AI wizard config for a practice
export function updateAIConfig(practiceId, data) {
    return async () => {
        dispatch(slice.actions.setAiWizardConfigLoading(true));
        const response = await callApi(`/api/v1/practices/${practiceId}/ai-config/`, 'PATCH', data);
        dispatch(slice.actions.setAiWizardConfigLoading(false));
        if (!response.ok) {
            return { ok: false, error: response.data?.error || response.data?.detail || 'Failed to save' };
        }
        dispatch(slice.actions.setAiWizardConfig(response.data));
        return { ok: true };
    };
}

// Legacy: Fetch AI config from practice choices (can be called independently without full WhatsApp init)
export function fetchAiConfig(practiceId) {
    return async () => {
        if (!practiceId) return;

        try {
            dispatch(slice.actions.setAiConfigLoading(true));
            const practiceChoices = await callApi(`/api/v1/practices/${practiceId}/choices/`);

            // Extract AI Config from practice choices
            const aiConfigChoice = practiceChoices?.data?.results?.find(choice => /ai\s*config/i.test(String(choice?.name || '')));
            const aiConfig = aiConfigChoice?.metadata || null;

            dispatch(slice.actions.setAiConfig(aiConfig));
        } catch (error) {
            console.error("Error fetching AI config", error);
        } finally {
            dispatch(slice.actions.setAiConfigLoading(false));
        }
    }
}

// Fetch template filter configs from practice choices (can be called independently without full WhatsApp init)
export function fetchTemplateFilterConfigs(practiceId) {
    return async () => {
        if (!practiceId) return;

        try {
            const practiceChoices = await callApi(`/api/v1/practices/${practiceId}/choices/?limit=200`);

            const waTemplateConfig = practiceChoices?.data?.results?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'whatsapp_template'
            );
            const allowedUsageTypes = waTemplateConfig?.items?.length > 0
                ? waTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            const emailTemplateConfig = practiceChoices?.data?.results?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'email_template'
            );
            const allowedEmailUsageTypes = emailTemplateConfig?.items?.length > 0
                ? emailTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            dispatch(slice.actions.setTemplateFilterConfigs({ allowedUsageTypes, allowedEmailUsageTypes }));
        } catch (error) {
            console.error("Error fetching template filter configs", error);
        }
    }
}

export function updateSubChannel(subs) {
    return async () => {
        const pieSocketSubscriptions = store.getState()?.whatsappV2?.pieSocketSubscriptions;
        dispatch(slice.actions.updatePieSocketSubscriptions([...pieSocketSubscriptions, subs]));
    }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// eslint-disable-next-line no-unused-vars
const getCampaigns = async () => {
    try {
        const practiceId = getPracticeId();
        const api = `/api/v1/practices/${practiceId}/campaigns/?&channel=WHATSAPP&ordering=-created_at&status__in=SENT`;
        const response = await callApi(api, 'GET');
        const idMap = response?.data?.results?.map(obj => obj?.id)?.join(",");
        if (idMap?.length) return idMap;
        return -1;
    } catch (error) {
        console.error("error in campaigns");
        return "-1";
    }
}

export function getWhatsaAppThreads(fetchNext = false, retryAttempt = 0) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        try {
            const practiceId = getPracticeId();
            const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
            const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;
            const currentSearch = store.getState()?.whatsappV2?.currentSearch;
            const threadNextAPI = store.getState()?.whatsappV2?.threadNextAPI;
            const currentThreadMessage = store.getState()?.whatsappV2?.threadMessages;
            const searchMode = store.getState()?.whatsappV2?.searchMode;
            const searchTag = store.getState()?.whatsappV2?.currentTag;
            const isLoading = store.getState()?.whatsappV2?.isLoading;
            const currentUser = store.getState()?.whatsappV2?.currentUser;
            let threadMessages = [];
            let threadNext = "";

            const isBusinessInvalid = !currentBusinessId || currentBusinessId === -1;
            const isPhoneInvalid = !currentWhatsappPhoneId || currentWhatsappPhoneId === -1;
            const whatsappNotConfigured = store.getState()?.whatsappV2?.whatsappNotConfigured;

            if ((isBusinessInvalid || isPhoneInvalid) && !isLoading && whatsappNotConfigured) {
                // Practice has no WhatsApp Business account configured — show the empty state, no error
            } else if (((isBusinessInvalid || isPhoneInvalid) && !isLoading) && retryAttempt <= 3) {
                dispatch(updateError({ message: "Loading whatsapp messages", success: "warning" }));
                await wait(2000 * retryAttempt);
                dispatch(getWhatsaAppThreads(fetchNext, retryAttempt + 1));
            } else if ((isBusinessInvalid || isPhoneInvalid) && !isLoading) {
                dispatch(updateError({ message: "Please contact support! Configuration not present!", success: false }));
            } else if (isLoading && retryAttempt <= 3) {
                await wait(2000 * retryAttempt);
                dispatch(getWhatsaAppThreads(fetchNext, retryAttempt + 1));
            } else if (isLoading) {
                throw new Error("Stuck in Loading");
            } else if (fetchNext) {
                fetchFilterCounts(false);
                dispatch(slice.actions.startThreadNextFetch());
                const result = threadNextAPI.match(/api\/v1.*/);
                const extractedPath = result ? result[0] : null;
                const threadResponse = await callApi(`/${extractedPath}`, 'GET');
                threadMessages = [...currentThreadMessage, ...(threadResponse?.data?.results ?? [])];
                threadNext = threadResponse?.data?.next ?? "";
                dispatch(slice.actions.completeThreadNextFetch({
                    threadMessages,
                    threadNext
                }))
            } else {
                fetchFilterCounts();
                dispatch(slice.actions.startFetchWhatsappThreads());
                const THREADS_PAGE_LIMIT = 50;
                const modeParams = SEARCH_MODE_PARAMS[searchMode] || SEARCH_MODE_PARAMS[''];
                let params = `&limit=${THREADS_PAGE_LIMIT}&is_archived_included=true&ordering=whatsapp_messages__created_time${modeParams}`;

                if (searchMode === "TAG" && searchTag?.length) {
                    params = `${params}&tagged_items__tag__name__in=${searchTag}`;
                }
                if (searchMode === "MY") {
                    params = `${params}&lead__assignees__user__id=${currentUser?.pk}`;
                }
                let api = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/?${params}`;
                if (currentSearch?.length) {
                    api = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/search/?${params}&query=${currentSearch}`;
                }
                const threadResponse = await callApi(api, 'GET');
                threadMessages = threadResponse?.data?.results ?? [];
                threadNext = threadResponse?.data?.next ?? "";
                dispatch(slice.actions.completeFetchWhatsappThreads({
                    threadMessages,
                    threadNext
                }))
            }
        } catch (error) {
            console.error("Error in initialize Threads", error);
            dispatch(updateError({ message: "Failed to get Whatsapp Messages", success: false }));
            if (fetchNext) {
                dispatch(slice.actions.completeThreadNextFetch({
                    threadMessages: [],
                    threadNext: ""
                }))
            } else {
                dispatch(slice.actions.completeFetchWhatsappThreads({
                    threadMessages: [],
                    threadNext: ""
                }))
            }
        }
    }
}

export function searchContactFromLeadRepo(searchKey) {
    return async () => {
        try {
            if (searchKey?.length) {
                dispatch(slice.actions.startLeadSearchResults());
                const practiceId = getPracticeId();
                const response = await callApi(
                    `/api/v1/practices/${practiceId}/leads/search/?query=${searchKey}&offset=0&ordering=-created_at`
                );
                dispatch(slice.actions.storeLeadSearchResults({ data: response?.data?.results ?? [] }))
            } else {
                dispatch(slice.actions.storeLeadSearchResults({ data: [] }))
            }
        } catch (error) {
            console.error("Error in searching leads", error);
            dispatch(updateError({ message: "Failed to search contacts, please try after sometime!", success: false }));
            dispatch(slice.actions.storeLeadSearchResults({ data: [] }));
        }
    }
}

export function fetchWhatsappChatAndOtherMetaData(urlThreadId = null) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        try {
            dispatch(slice.actions.startWhatsappChatLoad());
            const practiceId = getPracticeId();
            let state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not initialized
            if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
                console.warn('fetchWhatsappChatAndOtherMetaData skipped: WhatsApp IDs not initialized');
                dispatch(slice.actions.errorWhatsappChatLoad());
                return;
            }

            // Direct-load path: when invoked with an explicit URL threadId
            // (e.g. user pasted /whatsapp/thread/123 into the address bar
            // and the parent's thread-list-prefetch couldn't seed the chat
            // — different phone profile, closed/archived filtered out, slow
            // init, etc.), fetch the thread by id first and seed metadata
            // before continuing into the standard flow. Skip when metadata
            // already points at the same thread.
            const existingMetaThreadId = state?.whatsAppChatMetaData?.thread?.id;
            if (urlThreadId && String(existingMetaThreadId) !== String(urlThreadId)) {
                const threadResponse = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${urlThreadId}/?is_archived_included=true`,
                    'GET'
                );
                if (threadResponse?.status === 200 && threadResponse?.data?.id) {
                    const fetchedThread = threadResponse.data;
                    const { avatar, name, phoneNumber, isCloseObject } = fetchContactDetails(fetchedThread);
                    dispatch(slice.actions.saveWhatsappChatMetaData({
                        leadId: fetchedThread?.lead?.id,
                        thread: fetchedThread,
                        name,
                        phoneNumber,
                        avatar,
                        isCloseObject,
                    }));
                    // Re-read state so the rest of the function sees the
                    // freshly seeded metadata.
                    state = store.getState()?.whatsappV2;
                } else {
                    dispatch(updateError({
                        message: "Failed to open chat - thread not found",
                        success: false,
                    }));
                    dispatch(slice.actions.errorWhatsappChatLoad());
                    return;
                }
            }

            const whatsAppChatMetaData = state?.whatsAppChatMetaData;
            const { leadId, thread } = whatsAppChatMetaData ?? {};
            if (leadId && thread) {
                dispatch(markAsRead(thread?.id, false));
                // Chat found in history and existing lead
                const whatsappChats = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                    'GET'
                );
                // Getting lead Details
                let leadData = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                    'GET'
                );
                if (leadData?.status !== 200) {
                    leadData = await callApi(
                        `/api/v1/practices/${practiceId}/archived/${leadId}/`,
                        'GET'
                    );
                }
                const whatsappTags = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/tags/?is_archived_included=true`,
                    'GET'
                );
                dispatch(slice.actions.completeWhatsapChatLoad({
                    whatsappChats: whatsappChats?.data?.results ?? [],
                    whatsAppChatNextAPI: whatsappChats?.data?.next,
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        leadDetails: leadData?.data,
                        tags: whatsappTags?.data?.results ?? []
                    }
                }));
            }
            else if (!leadId && thread) {
                dispatch(markAsRead(thread?.id, false));
                // Chat found in history but is a new lead
                let whatsappMessages = {}
                if (thread?.id) {
                    whatsappMessages = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                }
                const whatsappTags = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/tags/?is_archived_included=true`,
                    'GET'
                );
                dispatch(slice.actions.completeWhatsapChatLoad({
                    whatsappChats: whatsappMessages?.data?.results ?? [],
                    whatsAppChatNextAPI: whatsappMessages?.data?.next,
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        leadDetails: {},
                        tags: whatsappTags?.data?.results ?? []
                    }
                }));
            } else if (leadId && !thread) {
                // Chat might be in history but is an existing lead.
                // Use exact lead_id filter (NOT fuzzy phone search) to find the
                // thread — fuzzy search matches wrong threads when two leads
                // share similar phone number prefixes.
                let leadData = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                    'GET'
                );
                if (leadData?.status !== 200) {
                    leadData = await callApi(
                        `/api/v1/practices/${practiceId}/archived/${leadId}/`,
                        'GET'
                    );
                }
                // Find thread by exact lead FK — no ambiguity possible
                const threadResponse = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/?lead=${leadId}&is_archived_included=true&ordering=-created_at`,
                    'GET'
                );
                let matchedThread = threadResponse?.data?.results?.[0];

                // Fallback: if no thread linked by lead FK, search by phone
                // (for leads created before thread-lead linking was implemented)
                if (!matchedThread) {
                    let phoneNumber = leadData?.data?.lead_contact?.phone;
                    phoneNumber = phoneNumber?.trim().replaceAll(" ", "").replaceAll("+", "");
                    if (phoneNumber) {
                        const fallbackResponse = await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/search/?query=${phoneNumber}&is_archived_included=true&offset=0&ordering=-created_at`,
                            'GET'
                        );
                        // Only use fallback if the thread has no lead linked
                        // (prevents stealing another lead's thread)
                        const fallbackThread = fallbackResponse?.data?.results?.[0];
                        if (fallbackThread && !fallbackThread.lead?.id) {
                            matchedThread = fallbackThread;
                        }
                    }
                }

                const foundThread = matchedThread ?? {};
                let whatsappMessages = {}
                if (foundThread?.id) {
                    whatsappMessages = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${foundThread?.id}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                }
                dispatch(slice.actions.completeWhatsapChatLoad({
                    whatsappChats: whatsappMessages?.data?.results ?? [],
                    whatsAppChatNextAPI: whatsappMessages?.data?.next,
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        thread: foundThread,
                        leadDetails: leadData?.data
                    }
                }));
            } else {
                console.error("No Lead id or Thread Found");
                dispatch(slice.actions.errorWhatsappChatLoad());
            }
        } catch (error) {
            console.error("Error in getting details for this lead", error);
            dispatch(updateError({ message: "Failed to get chats, please try after sometime", success: false }));
            dispatch(slice.actions.errorWhatsappChatLoad());
        }
    }
}

export function sendWhatsappMessage(message, messageType, callbackFnc, { onSuccess, onFailure, repliedToWamid } = {}) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData } = state;

        // Guard: Don't make API calls if WhatsApp account/phone IDs are not initialized
        if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
            console.warn('sendWhatsappMessage skipped: WhatsApp IDs not initialized');
            dispatch(updateError({ message: "WhatsApp is still loading, please try again", success: "warning" }));
            return;
        }

        // Synchronous latch keyed by (thread, body). Closes before any await
        // so a held-Enter / keyboard auto-repeat second invocation 18-30 ms
        // later short-circuits before it can race the BE.
        const inflightKey = `${whatsAppChatMetaData?.thread?.id ?? 'new'}|${messageType ?? 'text'}|${(message ?? '').slice(0, 256)}`;
        if (waSendInflightKeys.has(inflightKey)) {
            return;
        }
        waSendInflightKeys.add(inflightKey);
        // BE idempotency key — same UUID per logical Send click. BE caches
        // for 60s and replays the original message on a duplicate POST.
        const clientMessageId = waGenIdemKey();

        const data = {};
        const threadId = whatsAppChatMetaData?.thread?.id;
        const leadId = whatsAppChatMetaData?.leadDetails?.id;
        const threadLeadId = whatsAppChatMetaData?.thread?.lead?.id;  // Lead ID from thread metadata
        let phoneNumberToSend = whatsAppChatMetaData?.phoneNumber;
        try {
            dispatch(slice.actions.toggleWhatsappSendLoading(true));
            // Patch phone number if needed
            if (phoneNumberToSend?.length === 10 && leadId) {
                phoneNumberToSend = `44${phoneNumberToSend}`;
                await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'PATCH', {
                    id: leadId,
                    lead_contact: { phone: phoneNumberToSend }
                });
            }

            data.message_type = messageType ?? 'text';
            data.to = phoneNumberToSend;
            if (messageType === 'template') data.template_name = message;
            else data.message = message;
            // GDPR FIX: Always include lead_id if thread has a linked lead
            // Use leadDetails.id if available, otherwise fall back to thread.lead.id
            // This ensures GDPR checks run even if leadDetails hasn't loaded yet
            const effectiveLeadId = leadId || threadLeadId;
            if (effectiveLeadId) data.lead_id = effectiveLeadId;
            // Reply-to-quote: when the operator clicked Reply on a bubble,
            // forward the parent wamid so the backend embeds Meta's
            // ``context.message_id`` and the message renders as a threaded
            // reply on the patient's device.
            if (repliedToWamid) data.replied_to_wamid = repliedToWamid;
            data.client_message_id = clientMessageId;

            // Parallel fetch template context
            if (messageType === 'template') {
                const [practiceCtxRes, leadCtxRes, treatmentsRes] = await Promise.all([
                    callApi(`/api/v1/practices/${practiceId}/get_context/`, 'GET'),
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/get_context/`, 'GET'),
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/`)
                ]);

                const treatmentIds = treatmentsRes?.data?.results?.map(t => t?.id) ?? [];

                const treatmentContexts = await Promise.all(
                    treatmentIds.map(id =>
                        callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/get_context/`)
                    )
                );

                const treatmentContextCombined = Object.assign({}, ...treatmentContexts.map(t => t?.data ?? {}));

                data.template_context = {
                    ...practiceCtxRes?.data,
                    ...leadCtxRes?.data,
                    ...treatmentContextCombined,
                };
            }

            // Send final message
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/send_message/`,
                'POST',
                data
            );

            const resId = response?.data?.thread?.id;
            const threadData = response?.data?.thread;

            dispatch(slice.actions.toggleWhatsappSendLoading(false));

            // Optimization: Use fresh data from send_message response instead of immediate refresh
            // Backend now returns updated contacted_count and treatment data synchronously
            if (response?.status === 200 && threadData) {
                onSuccess?.();
                // Update thread list with fresh data
                const currentThreads = getWhatsappThreadsFromStore() ?? [];
                const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData.id ?? -1}`);
                const updatedThreads = [threadData, ...filtered];
                dispatch(slice.actions.saveThreadDetails(updatedThreads));

                // Update chat metadata if this is the active conversation
                if ((`${threadId}` === `${threadData.id}`) || (!threadId && resId)) {
                    // Conditionally fetch fresh lead data only if a fallback treatment was created
                    // This reduces unnecessary API calls when no treatment changes occurred
                    const fallbackTreatmentCreated = response?.data?.fallback_treatment_created ?? false;
                    if (leadId && fallbackTreatmentCreated) {
                        const leadResponse = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'GET');
                        if (leadResponse?.status === 200) {
                            dispatch(slice.actions.saveLeadDetails({
                                ...whatsAppChatMetaData,
                                leadDetails: leadResponse?.data ?? {},
                            }));
                        }
                    }

                    // Refresh messages to show the newly sent message
                    const messagesResponse = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId ?? resId}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                    if (messagesResponse?.status === 200) {
                        dispatch(slice.actions.saveMessageDetails(messagesResponse?.data?.results ?? []));
                    }
                }

                // Update filter counts
                fetchFilterCounts(false);
            } else if (response?.status === 403) {
                // GDPR compliance: lead is not open for communication
                onFailure?.();
                dispatch(updateError({
                    message: "This lead is not available for communication. They may have been archived or opted out.",
                    success: false,
                    timeout: 5000
                }));
            } else if (response?.status === 409 && response?.data?.code === 'service_window_closed') {
                // PMP rules — Meta will not deliver free-form messages outside
                // the 24-hour service window. Show a structured dialog (not a
                // toast — the operator needs the "switch to template" CTA in
                // a sticky surface, not a dismissable corner pop) so they
                // can act without losing the typed draft.
                onFailure?.();
                dispatch(slice.actions.showServiceWindowClosedDialog({
                    error: response?.data?.error
                        || "WhatsApp service window is closed. Send an approved template instead.",
                    serviceWindow: response?.data?.service_window || null,
                    draft: messageType === 'template' ? null : message,
                }));
            } else if (!threadId && callbackFnc && !resId) {
                onFailure?.();
                dispatch(updateError({
                    message: "Please reopen latest thread to view messages!",
                    success: "warning"
                }));
                callbackFnc?.();
            } else if (response?.status !== 200) {
                // Show the actual Meta API error from our backend (e.g. "Template paused",
                // "Rate limit exceeded", "Number not on WhatsApp") instead of a generic message.
                // Backend returns { success: false, error: "Failed to send message: <detail>" }
                onFailure?.();
                const apiError = response?.data?.error || response?.data?.message;
                dispatch(updateError({
                    message: apiError || "Failed to send WhatsApp message. Please try again.",
                    success: false,
                    timeout: 8000,
                }));
            }

            dispatch(markAsRead(threadId ?? resId, false));
        } catch (error) {
            console.error("Error in sending Whatsapp Message", error);
            onFailure?.();
            dispatch(updateError({
                message: "Failed to send WhatsApp message. Please check your connection and try again.",
                success: false,
            }));
            dispatch(slice.actions.toggleWhatsappSendLoading(false));
            dispatch(refreshThreadAndMessageInfo(threadId, leadId));
        } finally {
            // Always release the latch — including on error — so the operator
            // can retry a failed send without a hard reload.
            waSendInflightKeys.delete(inflightKey);
        }
    };
}

// Track D / F3 — Forward N messages to one target thread.
// Calls the BE forward-messages endpoint, then refreshes the target
// thread's message list so the forwarded messages appear with the
// "Forwarded" attribution label. Returns the API response so the caller
// can decide whether to navigate to the target thread on success.
export function forwardMessages({ messageIds, targetThreadId, onSuccess, onFailure } = {}) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData } = state;

        if (!currentBusinessId || currentBusinessId === -1
            || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
            dispatch(updateError({
                message: 'WhatsApp is still loading, please try again',
                success: 'warning',
            }));
            onFailure?.();
            return null;
        }
        if (!Array.isArray(messageIds) || !messageIds.length || !targetThreadId) {
            dispatch(updateError({
                message: 'Pick at least one message and a target thread to forward',
                success: 'warning',
            }));
            onFailure?.();
            return null;
        }

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/forward-messages/`,
                'POST',
                { message_ids: messageIds, target_thread_id: targetThreadId },
            );

            if (response?.status === 200 && response?.data?.success) {
                const forwardedCount = response?.data?.forwarded?.length || 0;
                const skipped = response?.data?.errors?.length || 0;
                dispatch(updateError({
                    message: skipped
                        ? `Forwarded ${forwardedCount}, ${skipped} skipped`
                        : `Forwarded ${forwardedCount} message${forwardedCount === 1 ? '' : 's'}`,
                    success: skipped ? 'warning' : true,
                }));

                // Optimistic refresh — only refresh the target thread's
                // chat panel if it's the one currently open. Otherwise no
                // op (the operator will see them next time they open it).
                const openThreadId = whatsAppChatMetaData?.thread?.id;
                if (`${openThreadId}` === `${targetThreadId}`) {
                    dispatch(refreshThreadAndMessageInfo(targetThreadId, null, true));
                }

                onSuccess?.(response.data);
                return response.data;
            }

            // service_window_closed → 409 with a code, surface as warning
            if (response?.status === 409 && response?.data?.error === 'service_window_closed') {
                dispatch(updateError({
                    message: response?.data?.message
                        || 'Cannot forward: the 24-hour customer service window for the target chat is closed.',
                    success: 'warning',
                    timeout: 8000,
                }));
                onFailure?.(response.data);
                return response.data;
            }

            const apiError = response?.data?.error || response?.data?.message;
            dispatch(updateError({
                message: apiError || 'Failed to forward messages. Please try again.',
                success: false,
                timeout: 8000,
            }));
            onFailure?.(response?.data);
            return response?.data ?? null;
        } catch (err) {
            console.error('forwardMessages error', err);
            dispatch(updateError({
                message: 'Failed to forward messages. Please check your connection and try again.',
                success: false,
            }));
            onFailure?.();
            return null;
        }
    };
}

export function sendWhatsappMediaMessage(file, caption) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData } = state;

        // Guard: Don't make API calls if WhatsApp account/phone IDs are not initialized
        if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
            console.warn('sendWhatsappMediaMessage skipped: WhatsApp IDs not initialized');
            dispatch(updateError({ message: "WhatsApp is still loading, please try again", success: "warning" }));
            return;
        }

        const threadId = whatsAppChatMetaData?.thread?.id;
        const leadId = whatsAppChatMetaData?.leadDetails?.id;
        const threadLeadId = whatsAppChatMetaData?.thread?.lead?.id;
        const phoneNumberToSend = whatsAppChatMetaData?.phoneNumber;

        try {
            dispatch(slice.actions.toggleWhatsappSendLoading(true));

            // Step 1: Upload the file
            const formData = new FormData();
            formData.append('file', file);

            const uploadResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/upload_media/`,
                'POST',
                formData
            );

            if (!uploadResponse?.ok) {
                dispatch(updateError({ message: "Failed to upload file, please try again", success: false }));
                dispatch(slice.actions.toggleWhatsappSendLoading(false));
                return;
            }

            const { url: mediaUrl, filename: mediaFilename, mime_type: mimeType } = uploadResponse.data;

            // Step 2: Determine message type from mime_type
            let messageType = 'document';
            if (mimeType && mimeType.includes('image')) messageType = 'image';
            else if (mimeType && mimeType.includes('video')) messageType = 'video';

            // Step 3: Send the message with media
            const data = {
                message_type: messageType,
                to: phoneNumberToSend,
                media_url: mediaUrl,
                media_mime_type: mimeType,
            };

            if (caption) data.media_caption = caption;
            if (messageType === 'document') data.media_filename = mediaFilename;

            // GDPR FIX: Always include lead_id if thread has a linked lead
            const effectiveLeadId = leadId || threadLeadId;
            if (effectiveLeadId) data.lead_id = effectiveLeadId;

            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/send_message/`,
                'POST',
                data
            );

            const resId = response?.data?.thread?.id;
            const threadData = response?.data?.thread;

            dispatch(slice.actions.toggleWhatsappSendLoading(false));

            if (response?.status === 200 && threadData) {
                // Update thread list with fresh data
                const currentThreads = getWhatsappThreadsFromStore() ?? [];
                const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData.id ?? -1}`);
                const updatedThreads = [threadData, ...filtered];
                dispatch(slice.actions.saveThreadDetails(updatedThreads));

                // Update chat metadata if this is the active conversation
                if ((`${threadId}` === `${threadData.id}`) || (!threadId && resId)) {
                    // Refresh messages to show the newly sent message
                    const messagesResponse = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId ?? resId}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                    if (messagesResponse?.status === 200) {
                        dispatch(slice.actions.saveMessageDetails(messagesResponse?.data?.results ?? []));
                    }
                }

                // Update filter counts
                fetchFilterCounts(false);
            } else if (response?.status === 403) {
                dispatch(updateError({
                    message: "This lead is not available for communication. They may have been archived or opted out.",
                    success: false,
                    timeout: 5000
                }));
            } else if (response?.status !== 200) {
                // Show actual Meta API error for media sends too (same pattern as text sends)
                const apiError = response?.data?.error || response?.data?.message;
                dispatch(updateError({
                    message: apiError || "Failed to send media message. Please try again.",
                    success: false,
                    timeout: 8000,
                }));
            }

            dispatch(markAsRead(threadId ?? resId, false));
        } catch (error) {
            console.error("Error in sending WhatsApp media message", error);
            dispatch(updateError({
                message: "Failed to send media message, please try again!",
                success: "warning"
            }));
            dispatch(slice.actions.toggleWhatsappSendLoading(false));
            dispatch(refreshThreadAndMessageInfo(threadId, leadId));
        }
    };
}

const removeCurrentFromThreadAndUpdateInfo = async (currentThreads, threadId, refreshMessageDetails, whatsAppChatMetaData) => {
    try {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId } = state;

        // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
        if (!currentBusinessId || !currentWhatsappPhoneId) {
            console.warn('WhatsApp removeCurrentFromThread skipped: currentBusinessId or currentWhatsappPhoneId is undefined');
            return;
        }

        let updated;
        const response = await callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/?&is_archived_included=true`,
            'GET'
        );
        if (response?.status === 200) {
            const threadData = response?.data;
            // Removing current thread
            const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData.id ?? -1}`);
            updated = [...filtered];
            dispatch(slice.actions.saveThreadDetails(updated));
            console.error("Current filters did not support current thread");
            const isCloseObject = {
                isClosed: threadData?.is_closed,
                closedReason: threadData?.metadata?.close_reason
            }
            if (refreshMessageDetails) {
                dispatch(slice.actions.refreshTagInfo({
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        isCloseObject: isCloseObject ?? {}
                    }
                }));
            }
        } else {
            dispatch(updateError({
                message: "Please contact support! There is some discrepancy with the data for this thread",
                success: false
            }));
        }
    } catch (err) {
        dispatch(updateError({
            message: "Please contact support! There is some discrepancy with the data for this thread",
            success: false
        }));
    }
}

export function refreshThreadAndMessageInfo(threadId, leadId, refreshMessageDetails = true, patchThreadWithLeadInfo = false, newChat = false) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData, searchMode, currentTag: searchTag, currentUser } = state;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!currentBusinessId || !currentWhatsappPhoneId) {
                console.warn('WhatsApp refresh skipped: currentBusinessId or currentWhatsappPhoneId is undefined');
                return;
            }

            let params = `&is_archived_included=true`;

            if (searchMode === "TAG" && searchTag?.length) {
                params = `${params}&tagged_items__tag__name__in=${searchTag}`
            }
            if (searchMode === "CLOSED") {
                params = `${params}&is_closed=${true}`
            } else {
                params = `${params}&is_closed=${false}`
            }
            if (searchMode === "MY") {
                params = `${params}&lead__assignees__user__id=${currentUser?.pk}`
            }

            let whatsAppChatMetaDataClone = window.structuredClone(whatsAppChatMetaData);

            if (patchThreadWithLeadInfo) {
                await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/`,
                    'PATCH', {
                    lead: leadId
                }
                )
            }

            const messagePromise = refreshMessageDetails && (`${threadId}` === `${whatsAppChatMetaData?.thread?.id}` || newChat)
                ? callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                    'GET'
                )
                : null;

            const whatsappTags = refreshMessageDetails && (`${threadId}` === `${whatsAppChatMetaData?.thread?.id}` || newChat)
                ? callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/tags/?is_archived_included=true`,
                    'GET'
                )
                : null;

            const threadPromise = threadId
                ? callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/?${params}`,
                    'GET'
                )
                : null;

            const leadPromise = leadId
                ? callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'GET')
                : null;

            // Handle messages response independently
            if (messagePromise) {
                messagePromise
                    .then((res) => {
                        if (res?.status === 200)
                            dispatch(slice.actions.saveMessageDetails(res?.data?.results ?? []));
                        else
                            dispatch(updateError({
                                message: "Failed to get whatsapp messages !",
                                success: "warning"
                            }));
                    })
                    .catch((error) => {
                        console.error("Error fetching messages:", error);
                    });
            }

            // Handle Tags
            if (whatsappTags) {
                whatsappTags
                    .then((res) => {
                        if (res?.status === 200) {
                            whatsAppChatMetaDataClone = {
                                ...whatsAppChatMetaDataClone,
                                tags: res?.data?.results ?? []
                            }
                            dispatch(slice.actions.refreshTagInfo({
                                whatsAppChatMetaData: {
                                    ...whatsAppChatMetaDataClone
                                }
                            }));
                        }
                        else
                            dispatch(updateError({
                                message: "Failed to get whatsapp tags !",
                                success: "warning"
                            }));
                    })
                    .catch((error) => {
                        console.error("Error fetching messages:", error);
                    });
            }

            // Handle thread response independently
            if (threadPromise) {
                threadPromise
                    .then((res) => {
                        const threadData = res?.data;
                        const currentThreads = getWhatsappThreadsFromStore() ?? [];
                        const isCloseObject = {
                            isClosed: threadData?.is_closed,
                            closedReason: threadData?.metadata?.close_reason
                        }
                        if (refreshMessageDetails && threadData?.id) {
                            whatsAppChatMetaDataClone = {
                                ...whatsAppChatMetaDataClone,
                                isCloseObject: isCloseObject ?? {}
                            }
                            dispatch(slice.actions.refreshTagInfo({
                                whatsAppChatMetaData: {
                                    ...whatsAppChatMetaDataClone
                                }
                            }));
                        }
                        let updated;
                        if (res?.status === 200) {
                            if (refreshMessageDetails) {
                                // Move thread to top
                                const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData?.id ?? -1}`);
                                updated = [threadData, ...filtered];
                            } else {
                                // Update thread in-place
                                updated = currentThreads.map((t) =>
                                    t.id === threadData.id ? { ...t, ...threadData } : t
                                );
                            }
                            dispatch(slice.actions.saveThreadDetails(updated));
                        } else {
                            removeCurrentFromThreadAndUpdateInfo(currentThreads, threadId, refreshMessageDetails, whatsAppChatMetaDataClone);
                        }
                        fetchFilterCounts(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching thread:", error);
                    });
            }

            // Handle lead response independently
            if (leadPromise) {
                leadPromise
                    .then(async (res) => {
                        let leadData = res;
                        if (leadData?.status !== 200) {
                            leadData = await callApi(`/api/v1/practices/${practiceId}/archived/${leadId}/`, 'GET');
                        }
                        if (leadData?.status === 200) {
                            // Detect if backend moved lead to a new follow-up stage
                            const oldLead = whatsAppChatMetaData?.leadDetails;
                            const newLead = leadData?.data;
                            if (oldLead && newLead) {
                                try {
                                    const oldTreatments = oldLead?.lead_treatments ?? [];
                                    const newTreatments = newLead?.lead_treatments ?? [];
                                    const changedTreatment = newTreatments.find((newTreatment) => {
                                        const oldTreatment = oldTreatments.find(t => t.id === newTreatment.id);
                                        if (!oldTreatment) return false;
                                        const status = newTreatment?.status ?? oldTreatment?.status;
                                        const oldCount = oldTreatment?.lead_contacted_counts?.find(c => c.status === status)?.count ?? 0;
                                        const newCount = newTreatment?.lead_contacted_counts?.find(c => c.status === status)?.count ?? 0;
                                        return newCount > oldCount;
                                    });
                                    if (changedTreatment) {
                                        const status = changedTreatment?.status;
                                        const newCount = changedTreatment?.lead_contacted_counts?.find(c => c.status === status)?.count ?? 0;
                                        const leadName = `${newLead?.first_name ?? ''} ${newLead?.last_name ?? ''}`.trim() || 'Lead';
                                        const followUpLabel = newCount >= 3 ? 'Follow up 3+' : `Follow up ${newCount}`;
                                        dispatch(updateError({
                                            message: `${leadName} has been moved to ${followUpLabel}`,
                                            success: 'success',
                                            timeout: 5000,
                                        }));
                                    }
                                } catch (e) {
                                    console.error('Error checking follow-up status change:', e);
                                }
                            }

                            dispatch(
                                slice.actions.saveLeadDetails({
                                    ...whatsAppChatMetaData,
                                    leadDetails: leadData?.data ?? {},
                                })
                            );
                        } else {
                            dispatch(updateError({
                                message: "Please contact support! There is some discrepancy with the data with this lead",
                                success: false
                            }));
                        }
                    })
                    .catch((error) => {
                        console.error("Error fetching lead:", error);
                    });
            }
        } catch (error) {
            console.error("Unexpected error in refreshing thread and message info", error);
            dispatch(
                updateError({
                    message: "Failed to update chat! Please refresh",
                    success: "warning",
                })
            );
        }
    };
}

export function fetchMoreWhastappMessages() {
    return async () => {
        const state = store.getState()?.whatsappV2;
        const whatsAppChatNextAPI = state?.whatsAppChatNextAPI;
        const whatsappChats = state?.whatsappChats;
        try {
            dispatch(slice.actions.startWhatsappNextMessageFetch());
            const result = whatsAppChatNextAPI.match(/api\/v1.*/);
            const extractedPath = result ? result[0] : null;
            const threadResponse = await callApi(`/${extractedPath}`, 'GET');
            dispatch(slice.actions.completeWhatsappNextMessageFetch({
                whatsappChats: [...whatsappChats, ...(threadResponse?.data?.results ?? [])],
                whatsAppChatNextAPI: threadResponse?.data?.next
            }))
        } catch (error) {
            console.error("Error in finding next messages", error);
            dispatch(updateError({ message: "Failed to get older chats, please try after sometime", success: false }));
            dispatch(slice.actions.completeWhatsappNextMessageFetch({ whatsappChats, whatsAppChatNextAPI }))
        }
    }
}

export function markAsRead(threadId, refresh = true) {
    return async () => {
        try {
            if (threadId) {
                const practiceId = getPracticeId();
                const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
                const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;

                // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
                if (!currentBusinessId || !currentWhatsappPhoneId) {
                    return;
                }

                await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/mark_as_read/?is_archived_included=true`,
                    'POST'
                );
                dispatch(refreshThreadAndMessageInfo(threadId, null, refresh));
            }
        } catch (error) {
            console.error("Error in marking as read", error);
        }
    }
}

export function markAsUnRead(threadId) {
    return async () => {
        try {
            if (threadId) {
                const practiceId = getPracticeId();
                const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
                const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;

                // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
                if (!currentBusinessId || !currentWhatsappPhoneId) {
                    return;
                }

                const response = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/mark_as_unread/?is_archived_included=true`,
                    'POST'
                );
                if (!response?.ok) {
                    // Tier 3 fixup: surface 4xx/5xx to the operator instead
                    // of silently succeeding the optimistic UI update.
                    dispatch(updateError({
                        success: false,
                        message: response?.data?.error || 'Could not mark as unread',
                    }));
                    return;
                }
                dispatch(refreshThreadAndMessageInfo(threadId));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Could not mark as unread' }));
            console.error("Error in marking as unread", error);
        }
    }
}

export function addWhatsappTag(threadId, tagName, tagColor) {
    return async () => {
        try {
            if (threadId) {
                const practiceId = getPracticeId();
                const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
                const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;

                // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
                if (!currentBusinessId || !currentWhatsappPhoneId) {
                    return;
                }

                const whatsAppChatMetaData = store.getState()?.whatsappV2?.whatsAppChatMetaData;
                whatsAppChatMetaData?.tags?.forEach(tagObject => {
                    callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/untag/`,
                        'POST',
                        {
                            name: tagObject?.name,
                        }
                    )
                })
                await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/tag/`,
                    'POST',
                    {
                        name: tagName,
                        colour: tagColor
                    }
                );
                dispatch(refreshThreadAndMessageInfo(threadId, null, true));
                dispatch(updateError({ message: "Successfully Added Tag to Whatsapp Thread", success: true }));
            }
        } catch (error) {
            dispatch(updateError({ message: "Failed to add tag to the thread", success: false }));
            console.error("Error in adding tags", error);
        }
    }
}

export function removeWhatsappTag(threadId) {
    return async () => {
        try {
            if (!threadId) return;

            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!currentBusinessId || !currentWhatsappPhoneId) {
                return;
            }

            const tags = state?.whatsAppChatMetaData?.tags ?? [];

            if (!Array.isArray(tags) || tags.length === 0) {
                dispatch(updateError({ message: "No tags to remove", success: true }));
                return;
            }

            const base = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}`;

            // Fire untag calls in parallel and wait for all to settle
            const untagPromises = tags.map((tagObject) =>
                callApi(`${base}/untag/`, 'POST', { name: tagObject?.name })
            );

            const results = await Promise.allSettled(untagPromises);
            const failures = results.filter(r => r.status === 'rejected').length;

            // Ensure refresh only after all untag operations are complete
            await store.dispatch(refreshThreadAndMessageInfo(threadId, null, true));

            dispatch(updateError({
                message: failures ? `Removed tags with ${failures} failure(s)` : "Successfully Removed Tag(s)",
                success: failures === 0
            }));
        } catch (error) {
            dispatch(updateError({ message: "Failed to remove tag from the thread", success: false }));
            console.error("Error in removing tags", error);
        }
    }
}

export function closeWhatsappConversationWithReason(threadId, reason, leadId) {
    return async () => {
        try {
            if (!threadId) return;

            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!currentBusinessId || !currentWhatsappPhoneId) {
                return;
            }

            const url = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/?is_archived_included=true`;
            const response = await callApi(
                url,
                'PATCH', {
                is_closed: true,
                metadata: {
                    close_reason: reason
                }
            }
            );
            if (response?.status === 200) {
                dispatch(updateError({
                    message: `Conversation resolved — ${reason ?? ""}`,
                    success: true,
                    timeout: 6000,
                    undo: {
                        type: 'reopen_whatsapp',
                        threadId,
                        leadId,
                    }
                }));
                // Ensure refresh only after all untag operations are complete
                await store.dispatch(refreshThreadAndMessageInfo(threadId, null, true));
            } else {
                const detail = response?.data?.detail || response?.data?.message || '';
                const msg = detail
                    ? `Failed to close conversation: ${detail}`
                    : 'Failed to close whatsapp conversation. Please try again.';
                dispatch(updateError({ message: msg, success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: "Failed to close whatsapp conversation. Please try again.", success: false }));
            console.error("Failed to close whatsapp conversation", error);
        }
    }
}

export function reOpenWhatsappConversationWithReason(threadId, leadId) {
    return async () => {
        try {
            if (!threadId) return;

            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!currentBusinessId || !currentWhatsappPhoneId) {
                return;
            }

            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/`,
                'PATCH', {
                is_closed: false,
                metadata: {
                    closeReason: ""
                }
            }
            );
            if (response?.status === 200) {
                dispatch(updateError({
                    message: `Successfully reopened thread`,
                    success: true
                }));
                // Ensure refresh only after all untag operations are complete
                await store.dispatch(refreshThreadAndMessageInfo(threadId, null, true));
                const addNotesObj = {
                    note: "OTHER",
                    additional_notes: "Whatsapp Chat Reopened!",
                    lead: leadId
                };
                if (leadId)
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', addNotesObj);
            }
            else
                dispatch(updateError({
                    message: "Failed to reopen whatsapp conversation!",
                    success: false
                }));
        } catch (error) {
            dispatch(updateError({ message: "Failed to reopen whatsapp conversation", success: false }));
            console.error("Failed to reopen whatsapp conversation", error);
        }
    }
}

export function reactToMessage(threadId, messageId, emoji) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const currentBusinessId = state?.currentBusinessId;
        const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
        if (!practiceId || !threadId || !messageId || !currentBusinessId || !currentWhatsappPhoneId) return;

        const list = state?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId))?.whatsapp_message_reaction || null;

        const removed = !emoji;
        dispatch(applyMessageReaction({ messageId, emoji: removed ? null : emoji, removed }));

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/react/`,
                'POST',
                { emoji: removed ? '' : emoji },
            );
            if (!response?.ok) {
                dispatch(applyMessageReaction({
                    messageId,
                    emoji: previous?.emoji ?? null,
                    removed: !previous?.emoji,
                }));
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to send reaction',
                }));
            }
        } catch (error) {
            dispatch(applyMessageReaction({
                messageId,
                emoji: previous?.emoji ?? null,
                removed: !previous?.emoji,
            }));
            dispatch(updateError({ success: false, message: 'Failed to send reaction' }));
            console.error('Error sending reaction', error);
        }
    };
}

export function revokeMessage(threadId, messageId) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const currentBusinessId = state?.currentBusinessId;
        const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
        if (!practiceId || !threadId || !messageId || !currentBusinessId || !currentWhatsappPhoneId) return;

        const list = state?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId));
        const previousBody = previous?.body ?? '';
        const previousIsRevoked = Boolean(previous?.is_revoked);

        dispatch(applyMessageRevoked({ messageId }));

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/revoke/`,
                'DELETE',
            );
            if (!response?.ok) {
                if (previous && !previousIsRevoked) {
                    dispatch(revertMessageRevoked({ messageId, body: previousBody }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to delete message',
                }));
            }
        } catch (error) {
            if (previous && !previousIsRevoked) {
                dispatch(revertMessageRevoked({ messageId, body: previousBody }));
            }
            dispatch(updateError({ success: false, message: 'Failed to delete message' }));
            console.error('Error revoking message', error);
        }
    };
}

// ---------------------------------------------------------------------
// Track C — F6 Edit + F8 Message Info thunks
// ---------------------------------------------------------------------

// editMessage(threadId, messageId, newBody)
//
// Optimistic body swap → PATCH .../edit/ → on failure surgically revert.
// We capture the previous body BEFORE dispatching applyMessageEdited so the
// revert action can put it back exactly. The thunk does NOT toast on the
// success path because the PieSocket message-edited event is the
// source-of-truth UI update for any other operator viewing the same chat.
export function editMessage(threadId, messageId, newBody) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2 ?? {};
            const { currentBusinessId, currentWhatsappPhoneId } = state;

            if (!practiceId || !currentBusinessId || !currentWhatsappPhoneId) {
                dispatch(updateError({
                    message: 'WhatsApp context not initialised — please retry.',
                    success: false,
                }));
                return;
            }

            // Snapshot the prior shape so a server failure can put the bubble
            // back exactly as it was. This is the surgical-revert pattern
            // Track A's fixup established for reactions.
            const messages = state.whatsappChats ?? [];
            const prior = messages.find((m) => `${m?.id}` === `${messageId}`);
            const previousBody = prior?.body ?? '';
            const previousIsEdited = prior?.is_edited ?? false;
            const previousEditedAt = prior?.edited_at ?? null;

            dispatch(slice.actions.applyMessageEdited({
                messageId,
                newBody,
                editedAt: new Date().toISOString(),
            }));

            const url = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/edit/`;
            const res = await callApi(url, 'PATCH', { body: newBody });

            if (res?.status === 200) {
                // Server confirmed; PieSocket will fan out to every other
                // socket. No additional toast — silent success matches WA's
                // native edit affordance.
                return;
            }

            // Non-200: revert. Pull a friendly code/message from the payload.
            dispatch(slice.actions.revertMessageEdited({
                messageId,
                previousBody,
                previousIsEdited,
                previousEditedAt,
            }));
            const errorPayload = res?.data ?? {};
            const code = errorPayload?.code;
            const msg = errorPayload?.error || (
                code === 'edit_window_expired'
                    ? 'Edit window expired (15-minute Meta limit).'
                    : 'Failed to edit message.'
            );
            dispatch(updateError({ message: msg, success: false }));
        } catch (error) {
            console.error('Failed to edit whatsapp message', error);
            dispatch(updateError({ message: 'Failed to edit message.', success: false }));
        }
    };
}

// fetchMessageInfo(threadId, messageId)
//
// Lazy-fetched only when the operator opens the Info drawer. Backend never
// includes status_logs in the main message-list payload — that's intentional
// per D12 to keep chat-render cheap.
export function fetchMessageInfo(threadId, messageId) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2 ?? {};
            const { currentBusinessId, currentWhatsappPhoneId } = state;
            if (!practiceId || !currentBusinessId || !currentWhatsappPhoneId) return;

            dispatch(slice.actions.startMessageInfoFetch(messageId));

            const url = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/info/`;
            const res = await callApi(url, 'GET');
            if (res?.status === 200) {
                dispatch(slice.actions.completeMessageInfoFetch({
                    messageId,
                    info: {
                        sent_at: res?.data?.sent_at ?? null,
                        delivered_at: res?.data?.delivered_at ?? null,
                        read_at: res?.data?.read_at ?? null,
                        failed_at: res?.data?.failed_at ?? null,
                    },
                }));
            } else {
                dispatch(slice.actions.failMessageInfoFetch(messageId));
            }
        } catch (error) {
            console.error('Failed to fetch message info', error);
            dispatch(slice.actions.failMessageInfoFetch(messageId));
        }
    };
}

// AI Assistant thunk
export function generateAiResponse(actionType, context = {}) {
    return async () => {
        try {
            // Get AI config from state
            const state = store.getState();
            const aiConfig = state?.whatsappV2?.aiConfig || {};

            // Check if AI is enabled for WhatsApp
            if (aiConfig.enableAiForWhatsapp === false) {
                dispatch(slice.actions.completeAiAssistant({
                    content: 'AI assistance is currently disabled for WhatsApp. Please enable it in Practice Choices > AI Config.',
                    actionType: 'DISABLED',
                    isDisabled: true
                }));
                return;
            }

            dispatch(slice.actions.startAiAssistant(actionType));
            const practiceId = getPracticeId();

            // Build system prompt and messages based on action type, including AI config
            const { systemPrompt, messages } = buildAiPromptAndMessages(actionType, context, aiConfig);

            const response = await callApi(
                `/api/v1/practices/${practiceId}/ai/chat/`,
                'POST',
                {
                    system_prompt: systemPrompt,
                    messages
                },
                false, // isUnAuthorized
                { timeout: 60000 } // 60 second timeout for AI responses
            );

            if (response?.ok || response?.status === 200) {
                // Extract the AI response content from the API response
                const aiContent = response?.data?.choices?.[0]?.message?.content
                    || response?.data?.message?.content
                    || response?.data?.content
                    || response?.data?.response
                    || response?.data;

                dispatch(slice.actions.completeAiAssistant({
                    content: typeof aiContent === 'string' ? aiContent : JSON.stringify(aiContent),
                    actionType
                }));
            } else {
                const rawError = response?.data?.error || response?.data?.message || '';
                // Check for OpenRouter API key not configured error
                const isApiKeyError = rawError.toLowerCase().includes('openrouter') ||
                    rawError.toLowerCase().includes('api key not configured') ||
                    rawError.toLowerCase().includes('not configured');

                if (isApiKeyError) {
                    const infoMessage = 'AI is not configured for your practice. Please contact support to enable AI features.';
                    dispatch(slice.actions.completeAiAssistant({
                        content: infoMessage,
                        actionType: 'NOT_CONFIGURED',
                        isNotConfigured: true
                    }));
                    dispatch(updateError({
                        message: infoMessage,
                        success: 'warning'
                    }));
                } else {
                    const errorMessage = rawError || 'Failed to get AI response';
                    dispatch(slice.actions.failAiAssistant(errorMessage));
                    dispatch(updateError({
                        message: errorMessage,
                        success: false
                    }));
                }
            }
        } catch (error) {
            console.error("Error in AI Assistant", error);
            const rawError = error?.message || '';
            const isApiKeyError = rawError.toLowerCase().includes('openrouter') ||
                rawError.toLowerCase().includes('api key not configured') ||
                rawError.toLowerCase().includes('not configured');

            if (isApiKeyError) {
                const infoMessage = 'AI is not configured for your practice. Please contact support to enable AI features.';
                dispatch(slice.actions.completeAiAssistant({
                    content: infoMessage,
                    actionType: 'NOT_CONFIGURED',
                    isNotConfigured: true
                }));
                dispatch(updateError({
                    message: infoMessage,
                    success: 'warning'
                }));
            } else {
                const errorMessage = 'Failed to get AI response. Please try again.';
                dispatch(slice.actions.failAiAssistant(errorMessage));
                dispatch(updateError({
                    message: errorMessage,
                    success: false
                }));
            }
        }
    };
}

// Helper function to build AI prompts and messages
function buildAiPromptAndMessages(actionType, context, aiConfig = {}) {
    const { leadName, treatment, treatmentStatus, recentMessages, draftMessage, targetLanguage } = context;

    // Get the number of messages to use for context (from config, max 10)
    // Use slice(-messagesLimit) to get the LAST N messages (most recent), not the first N (oldest)
    const messagesLimit = Math.min(aiConfig.messagesPerNumberContext || 10, 10);
    const limitedMessages = recentMessages?.slice(-messagesLimit) || [];

    // Base context for all prompts
    const leadContext = leadName ? `Patient Name: ${leadName}` : '';
    const treatmentContext = treatment ? `Treatment: ${treatment}` : '';
    const statusContext = treatmentStatus ? `Treatment Status: ${treatmentStatus}` : '';
    const contextInfo = [leadContext, treatmentContext, statusContext].filter(Boolean).join('\n');

    // Format recent messages for context (limited by config)
    const conversationHistory = limitedMessages?.map(msg =>
        `${msg.role === 'patient' ? 'Patient' : 'Staff'}: ${msg.content}`
    ).join('\n') || '';

    // Build practice context from AI config
    const buildPracticeContext = () => {
        const sections = [];
        if (aiConfig.practiceInfo) {
            sections.push(`=== PRACTICE INFORMATION ===\n${aiConfig.practiceInfo}`);
        }
        if (aiConfig.treatmentInfo) {
            sections.push(`=== TREATMENT PROCESSES & STEPS ===\n${aiConfig.treatmentInfo}`);
        }
        if (aiConfig.pricingInfo) {
            sections.push(`=== PRICING INFORMATION ===\n${aiConfig.pricingInfo}`);
        }
        if (aiConfig.sopMessages) {
            sections.push(`=== STANDARD SOP MESSAGES ===\n${aiConfig.sopMessages}`);
        }
        if (aiConfig.communicationStyle) {
            sections.push(`=== COMMUNICATION STYLE GUIDELINES ===\n${aiConfig.communicationStyle}`);
        }
        return sections.length > 0 ? `\n\n--- PRACTICE KNOWLEDGE BASE ---\n${sections.join('\n\n')}` : '';
    };

    const practiceContext = buildPracticeContext();

    const prompts = {
        SMART_REPLY: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a professional, friendly WhatsApp reply based on the conversation context.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Keep responses concise and suitable for WhatsApp (under 300 characters when possible)
- Be warm, professional, and helpful
- Use the patient's name if available
- Address their specific concerns or questions
- Use the practice knowledge base to provide accurate information
- Do not use markdown formatting`,
            userMessage: `Based on this conversation, generate an appropriate reply:\n\n${conversationHistory}\n\n${draftMessage ? `Draft intent: ${draftMessage}` : 'Generate a helpful response to the last message.'}`
        },
        APPOINTMENT_REMINDER: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a friendly appointment reminder message.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Keep it brief and friendly
- Include treatment type if known
- Remind them to arrive 10 minutes early
- Follow the communication style from practice guidelines
- Do not use markdown formatting`,
            userMessage: `Generate a WhatsApp appointment reminder for this patient. ${conversationHistory ? `\n\nRecent conversation:\n${conversationHistory}` : ''}`
        },
        FOLLOW_UP: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a professional follow-up message for a patient who hasn't responded.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Be polite and not pushy
- Reference previous conversation if relevant
- Offer to help or answer questions
- Use SOP messages as reference where applicable
- Do not use markdown formatting`,
            userMessage: `Generate a follow-up message for this patient.\n\nRecent conversation:\n${conversationHistory}`
        },
        HANDLE_CONCERN: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate an empathetic response to a patient's concern (pricing, scheduling, anxiety, etc.).
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Be understanding and empathetic
- Address their specific concern
- Offer solutions or alternatives when possible
- Use practice pricing info and SOP messages as reference
- Reassure them without being dismissive
- Do not use markdown formatting`,
            userMessage: `Generate a compassionate response to this patient's concern:\n\n${conversationHistory}`
        },
        SUMMARIZE_CHAT: {
            systemPrompt: `You are a helpful assistant. Summarize this WhatsApp conversation from a dental practice.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}

Provide:
- Key discussion points
- Any decisions made
- Current status of the conversation
- Keep it concise (bullet points preferred)`,
            userMessage: `Summarize this conversation:\n\n${conversationHistory}`
        },
        PAIN_POINTS: {
            systemPrompt: `You are a helpful assistant. Extract and summarize the patient's pain points, concerns, and needs from this dental practice conversation.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}

Identify:
- Main concerns or complaints
- Hesitations or objections
- Questions they need answered
- Emotional state or anxiety indicators`,
            userMessage: `Extract the patient's pain points from this conversation:\n\n${conversationHistory}`
        },
        ACTION_ITEMS: {
            systemPrompt: `You are a helpful assistant. Extract action items and next steps from this dental practice conversation.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}

List:
- Tasks for staff to complete
- Follow-ups needed
- Patient commitments mentioned
- Deadlines or timeframes mentioned`,
            userMessage: `Extract action items from this conversation:\n\n${conversationHistory}`
        },
        EXPLAIN_TREATMENT: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a patient-friendly explanation of a dental treatment.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Use simple, non-technical language
- Explain benefits clearly
- Use the treatment information from the practice knowledge base
- Address common concerns
- Keep it reassuring and informative
- Do not use markdown formatting`,
            userMessage: `Generate a patient-friendly explanation of ${treatment || 'the dental treatment they inquired about'}.\n\n${conversationHistory ? `Context from conversation:\n${conversationHistory}` : ''}`
        },
        PRICING_INFO: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a professional message about pricing and payment options.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Be transparent and professional
- Use the pricing information from the practice knowledge base when available
- Mention financing options if applicable
- Invite them to discuss further or book a consultation for exact pricing
- Do not use markdown formatting`,
            userMessage: `Generate a message about pricing for ${treatment || 'dental services'}.\n\n${conversationHistory ? `Context:\n${conversationHistory}` : ''}`
        },
        IMPROVE_TONE: {
            systemPrompt: `You are a helpful assistant. Improve the tone of this message to be more professional and friendly while keeping the same meaning.
${aiConfig.communicationStyle ? `\nCommunication Style Guidelines:\n${aiConfig.communicationStyle}` : ''}

Guidelines:
- Maintain the original intent
- Make it warmer and more professional
- Follow the communication style guidelines if provided
- Fix any grammar issues
- Keep appropriate length
- Do not use markdown formatting`,
            // Use draftMessage if provided, otherwise get the LAST message (most recent) from the conversation
            userMessage: `Improve the tone of this message:\n\n${draftMessage || limitedMessages?.slice(-1)?.[0]?.content || 'No message to improve'}`
        },
        TRANSLATE: {
            systemPrompt: `You are a translation assistant. Translate the message to ${targetLanguage || 'English'}.

Guidelines:
- Maintain the tone and intent
- Use natural phrasing in the target language
- Keep formatting simple for WhatsApp`,
            // Use draftMessage if provided, otherwise get the LAST message (most recent) from the conversation
            userMessage: `Translate this to ${targetLanguage || 'English'}:\n\n${draftMessage || limitedMessages?.slice(-1)?.[0]?.content || 'No message to translate'}`
        }
    };

    const promptConfig = prompts[actionType] || prompts.SMART_REPLY;

    return {
        systemPrompt: promptConfig.systemPrompt,
        messages: [
            { role: 'user', content: promptConfig.userMessage }
        ]
    };
}

// Star + Pin thunks (Track B). All thunks pull threadId from the caller
// and pull practiceId / business / phone from store.getState() so the
// caller doesn't have to thread three more args through.

const buildMessageUrl = (threadId, messageId, suffix) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const businessId = state?.currentBusinessId;
    const phoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !threadId || !messageId || !businessId || !phoneId) return null;
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/${suffix}/`;
};

const buildThreadUrl = (threadId, suffix) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const businessId = state?.currentBusinessId;
    const phoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !threadId || !businessId || !phoneId) return null;
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/${threadId}/${suffix}/`;
};

export function starMessage(threadId, messageId) {
    return async () => {
        const url = buildMessageUrl(threadId, messageId, 'star');
        if (!url) return;

        const list = store.getState()?.whatsappV2?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId));
        const previouslyStarred = Boolean(previous?.is_starred);

        dispatch(applyMessageStarred({ messageId }));

        try {
            const response = await callApi(url, 'POST', {});
            if (!response?.ok) {
                if (!previouslyStarred) {
                    dispatch(applyMessageUnstarred({ messageId }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to star message',
                }));
            } else if (response?.data?.starred_by) {
                dispatch(applyMessageStarred({ messageId, starredBy: response.data.starred_by }));
            }
        } catch (error) {
            if (!previouslyStarred) {
                dispatch(applyMessageUnstarred({ messageId }));
            }
            dispatch(updateError({ success: false, message: 'Failed to star message' }));
            console.error('Error starring message', error);
        }
    };
}

export function unstarMessage(threadId, messageId) {
    return async () => {
        const url = buildMessageUrl(threadId, messageId, 'star');
        if (!url) return;

        const list = store.getState()?.whatsappV2?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId));
        const previousStarredBy = previous?.starred_by ?? null;
        const previouslyStarred = Boolean(previous?.is_starred);

        dispatch(applyMessageUnstarred({ messageId }));

        try {
            const response = await callApi(url, 'DELETE');
            if (!response?.ok) {
                if (previouslyStarred) {
                    dispatch(applyMessageStarred({ messageId, starredBy: previousStarredBy }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to unstar message',
                }));
            }
        } catch (error) {
            if (previouslyStarred) {
                dispatch(applyMessageStarred({ messageId, starredBy: previousStarredBy }));
            }
            dispatch(updateError({ success: false, message: 'Failed to unstar message' }));
            console.error('Error unstarring message', error);
        }
    };
}

export function pinMessage(threadId, messageId) {
    return async () => {
        const url = buildThreadUrl(threadId, 'pin');
        if (!url || !messageId) return;

        const meta = store.getState()?.whatsappV2?.whatsAppChatMetaData ?? {};
        const previousPinned = meta?.thread?.pinned_message ?? null;
        const previousPinnedAt = meta?.thread?.pinned_at ?? null;

        // Optimistic — replace the banner before the network round-trip so
        // the operator sees instant feedback. Surgical revert on error
        // restores the previous pin (or no pin).
        const list = store.getState()?.whatsappV2?.whatsappChats ?? [];
        const target = list.find((m) => String(m?.id) === String(messageId));
        dispatch(applyMessagePinned({
            messageId,
            threadId,
            pinnedMessage: target
                ? { id: target.id, body: target.content ?? target.body ?? '', from_waid: target.fromWaid ?? target.from_waid, created_time: target.created_time }
                : { id: messageId },
            pinnedAt: new Date().toISOString(),
        }));

        try {
            const response = await callApi(url, 'POST', { message_id: messageId });
            if (!response?.ok) {
                if (previousPinned) {
                    dispatch(applyMessagePinned({
                        messageId: previousPinned.id,
                        threadId,
                        pinnedMessage: previousPinned,
                        pinnedAt: previousPinnedAt,
                    }));
                } else {
                    dispatch(applyMessageUnpinned({ threadId }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to pin message',
                }));
            } else if (response?.data?.pinned_message) {
                dispatch(applyMessagePinned({
                    messageId: response.data.pinned_message.id,
                    threadId,
                    pinnedMessage: response.data.pinned_message,
                    pinnedAt: response.data.pinned_at,
                }));
            }
        } catch (error) {
            if (previousPinned) {
                dispatch(applyMessagePinned({
                    messageId: previousPinned.id,
                    threadId,
                    pinnedMessage: previousPinned,
                    pinnedAt: previousPinnedAt,
                }));
            } else {
                dispatch(applyMessageUnpinned({ threadId }));
            }
            dispatch(updateError({ success: false, message: 'Failed to pin message' }));
            console.error('Error pinning message', error);
        }
    };
}

export function unpinMessage(threadId) {
    return async () => {
        const url = buildThreadUrl(threadId, 'pin');
        if (!url) return;

        const meta = store.getState()?.whatsappV2?.whatsAppChatMetaData ?? {};
        const previousPinned = meta?.thread?.pinned_message ?? null;
        const previousPinnedAt = meta?.thread?.pinned_at ?? null;

        dispatch(applyMessageUnpinned({ threadId }));

        try {
            const response = await callApi(url, 'DELETE');
            if (!response?.ok) {
                if (previousPinned) {
                    dispatch(applyMessagePinned({
                        messageId: previousPinned.id,
                        threadId,
                        pinnedMessage: previousPinned,
                        pinnedAt: previousPinnedAt,
                    }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to unpin message',
                }));
            }
        } catch (error) {
            if (previousPinned) {
                dispatch(applyMessagePinned({
                    messageId: previousPinned.id,
                    threadId,
                    pinnedMessage: previousPinned,
                    pinnedAt: previousPinnedAt,
                }));
            }
            dispatch(updateError({ success: false, message: 'Failed to unpin message' }));
            console.error('Error unpinning message', error);
        }
    };
}

export function fetchStarredMessages(threadId) {
    return async () => {
        const url = buildThreadUrl(threadId, 'starred-messages');
        if (!url) return [];

        try {
            const response = await callApi(url, 'GET');
            if (response?.ok) {
                return response?.data?.results ?? [];
            }
            dispatch(updateError({
                success: false,
                message: response?.data?.error || 'Failed to load starred messages',
            }));
            return [];
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to load starred messages' }));
            console.error('Error fetching starred messages', error);
            return [];
        }
    };
}

// ---------------------------------------------------------------------
// Bulk thread actions (Track WA-bulk-archive-FE)
//
// These four thunks back the SidebarBulkActionBar. The BE endpoints all
// share a contract:
//   POST .../whatsapp-message-threads/<action>/
//   Body:    { thread_ids: [int, ...] }
//   200 OK:  { succeeded: [int], failed: [{ id, reason }] }
//   4xx:     permission denied or bad input
//
// Common patterns:
// - Each thunk returns the response so the caller can show an Undo
//   snackbar with the succeeded ids.
// - On non-OK, we surface a single error toast via `updateError` and
//   return `null` so the caller knows the operation failed.
// - On partial-success (succeeded.length > 0 && failed.length > 0) we
//   show a warning toast ("X succeeded, Y failed") so the operator
//   isn't blindsided by a quiet partial. Failed ids stay selected so
//   the operator can retry.
// - Optimistic updates run BEFORE the network round-trip ONLY when we
//   have very high confidence (mark_as_read / mark_as_unread). For
//   archive we wait for the response so we can correctly highlight
//   only the succeeded subset.
// ---------------------------------------------------------------------

const buildBulkThreadUrl = (action) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const currentBusinessId = state?.currentBusinessId;
    const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !currentBusinessId || !currentWhatsappPhoneId) {
        return null;
    }
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${action}/`;
};

const surfacePartialFailure = (response) => {
    const failed = response?.data?.failed ?? [];
    const succeeded = response?.data?.succeeded ?? [];
    if (failed.length > 0) {
        // Warning, not error — the succeeded ones did go through.
        dispatch(updateError({
            success: 'warning',
            message: `${succeeded.length} succeeded, ${failed.length} failed`,
            timeout: 5000,
        }));
    }
};

export function bulkArchiveThreads(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            // Hyphenated URL — the backend `@action(url_path="bulk-archive")`
            // is authoritative. DRF does NOT translate underscores to
            // hyphens, so `bulk_archive` would 404 on every request.
            const url = buildBulkThreadUrl('bulk-archive');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to archive conversations',
                }));
                return null;
            }
            const succeeded = response?.data?.succeeded ?? [];
            if (succeeded.length > 0) {
                dispatch(slice.actions.removeThreadsFromList(succeeded));
            }
            surfacePartialFailure(response);
            return response?.data ?? { succeeded, failed: [] };
        } catch (error) {
            console.error('bulkArchiveThreads error', error);
            dispatch(updateError({ success: false, message: 'Failed to archive conversations' }));
            return null;
        }
    };
}

export function bulkUnarchiveThreads(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const url = buildBulkThreadUrl('bulk-unarchive');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to restore conversations',
                }));
                return null;
            }
            // Refetch the list — the unarchived threads need to come
            // back into view (they were filtered out when archived).
            dispatch(getWhatsaAppThreads());
            surfacePartialFailure(response);
            return response?.data ?? null;
        } catch (error) {
            console.error('bulkUnarchiveThreads error', error);
            dispatch(updateError({ success: false, message: 'Failed to restore conversations' }));
            return null;
        }
    };
}

export function bulkMarkThreadsRead(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const url = buildBulkThreadUrl('bulk-mark-as-read');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to mark as read',
                }));
                return null;
            }
            const succeeded = response?.data?.succeeded ?? ids;
            dispatch(slice.actions.applyThreadsRead(succeeded));
            // Refresh the filter counts (unread badge in the nav).
            fetchFilterCounts(false);
            surfacePartialFailure(response);
            return response?.data ?? { succeeded, failed: [] };
        } catch (error) {
            console.error('bulkMarkThreadsRead error', error);
            dispatch(updateError({ success: false, message: 'Failed to mark as read' }));
            return null;
        }
    };
}

export function bulkMarkThreadsUnread(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const url = buildBulkThreadUrl('bulk-mark-as-unread');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to mark as unread',
                }));
                return null;
            }
            const succeeded = response?.data?.succeeded ?? ids;
            dispatch(slice.actions.applyThreadsUnread(succeeded));
            fetchFilterCounts(false);
            surfacePartialFailure(response);
            return response?.data ?? { succeeded, failed: [] };
        } catch (error) {
            console.error('bulkMarkThreadsUnread error', error);
            dispatch(updateError({ success: false, message: 'Failed to mark as unread' }));
            return null;
        }
    };
}
